// apps/frontend/public/embed/quiz-embed.global.ts

type QuizAIAPI = { open: () => void; close: () => void }
type QuizAIMessage =
  | { source: "quizAI"; type: "close" }
  | { source: "quizAI"; type: "request-close" }
  // tipos adicionais usados no fluxo:
  | { source: "quizAI"; type: "completed" }
  | { source: "quizAI"; type: "notification-request" }
  | { source: "quizAI"; type: "notification-result"; permission: NotificationPermission | "default" }
  | ({ source: "quizAI"; type: string } & Record<string, unknown>)

;(() => {
  const current = document.currentScript
  if (!(current instanceof HTMLScriptElement)) return

  // helpers
  const toNumber = (v: string | null | undefined, fallback: number): number => {
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
  }
  const trimSlash = (s: string) => s.replace(/\/+$|^\//g, "")

  const urlParamsDad = new URLSearchParams(window.location.search);
  const idSlug = urlParamsDad.get("idSlug");

  // data-*
  const slug = idSlug ?? ""
  const originRaw = current.dataset.origin ?? ""
  const origin = trimSlash(originRaw)
  const terms = current.dataset.terms ?? ""
  const privacyPolicy = current.dataset.privacyPolicy ?? ""
  const delay = toNumber(current.dataset.delay, 800)
  const zIndex = String(current.dataset.zindex ?? "2147483647")
  const showCloseBtn = true // botão de saída sempre visível

  // não reabrir para quem já finalizou
  const storageKey = `quizAI__completed__${slug}`
  if (localStorage.getItem(storageKey)) return

  if (!slug || !origin) {
    console.warn("[quizAI] data-quiz-slug e/ou data-origin ausentes.")
    return
  }

  const CONTAINER_ID = "quizAI__container"
  const IFRAME_ID = "quizAI__iframe"

  let container: HTMLDivElement | null = null
  let iframe: HTMLIFrameElement | null = null
  let opened = false
  let escHandler: ((e: KeyboardEvent) => void) | null = null
  let msgHandler: ((e: MessageEvent) => void) | null = null

  function buildUI() {
    // remove instância anterior (evita modal atrás do modal)
    const existing = document.getElementById(CONTAINER_ID)
    if (existing) existing.remove()

    // overlay/container
    container = document.createElement("div")
    container.id = CONTAINER_ID
    Object.assign(container.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      zIndex,
      display: "none",
      background: "rgba(0,0,0,0.5)",
      overflow: "auto",
      padding: "5vh 16px",
      boxSizing: "border-box",
    } as CSSStyleDeclaration)

    // card/modal
    const modal = document.createElement("div")
    Object.assign(modal.style, {
      margin: "0 auto",
      background: "#fff",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      width: "100%",
      maxWidth: "620px",
      overflow: "hidden",
      position: "relative",
      paddingTop: "12px", // respiro superior para conteúdo
    } as CSSStyleDeclaration)

    // botão sair (clean)
    if (showCloseBtn) {
      const closeBtn = document.createElement("button")
      closeBtn.type = "button"
      closeBtn.innerHTML =
        "<svg viewBox='0 0 20 20' fill='none' stroke='currentColor' stroke-width='2' class='w-5 h-5'><path stroke-linecap='round' stroke-linejoin='round' d='M6 6l8 8m0-8l-8 8'/></svg>"
      closeBtn.setAttribute("aria-label", "Fechar")
      Object.assign(closeBtn.style, {
        position: "absolute",
        top: "10px",
        right: "12px",
        width: "28px",
        height: "28px",
        background: "transparent",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        zIndex: "3",
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#6b7280",
        transition: "all 0.2s ease",
      } as CSSStyleDeclaration)
      closeBtn.addEventListener("mouseenter", () => {
        closeBtn.style.background = "#f3f4f6"
      })
      closeBtn.addEventListener("mouseleave", () => {
        closeBtn.style.background = "transparent"
      })
      closeBtn.addEventListener("click", hardClose)
      modal.appendChild(closeBtn)
    }

    // iframe
    iframe = document.createElement("iframe")
    iframe.id = IFRAME_ID
    iframe.title = "QuizAI"

    const qs = new URLSearchParams();
    qs.set("embed", "1");
    if (terms) qs.set("terms", terms);
    if (privacyPolicy) qs.set("privacyPolicy", privacyPolicy)

    iframe.src = `${origin}/question/${encodeURIComponent(slug)}?embed=1&terms=${encodeURIComponent(terms)}&privacyPolicy=${encodeURIComponent(privacyPolicy)}`

    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms allow-popups allow-modals")
    iframe.allowFullscreen = true
    Object.assign(iframe.style, {
      display: "block",
      width: "100%",
      height: "100vh",
      maxHeight: "80vh",
      border: "0",
    } as CSSStyleDeclaration)

    iframe.addEventListener("load", () => {
      modal.style.display = "block"
    })

    modal.appendChild(iframe)
    container.appendChild(modal)
    document.body.appendChild(container)

    // ESC para fechar
    escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && opened) hardClose()
    }
    document.addEventListener("keydown", escHandler)

    // Mensagens do iframe → pai
    msgHandler = (ev: MessageEvent) => {
      const data = ev?.data as Partial<QuizAIMessage> | undefined
      if (!data || data.source !== "quizAI") return

      // fechar por comando
      if (data.type === "close") {
        hardClose()
        return
      }

      // concluiu cadastro → não reabrir
      if (data.type === "completed") {
        localStorage.setItem(storageKey, "1")
        hardClose()
        return
      }

      // **NOVO**: iframe pediu para solicitar permissão de notificação
      if (data.type === "notification-request") {
        const ask = async () => {
          let permission: NotificationPermission | "default" = "default"

          try {
            // Se o site pai (WordPress) expõe esta função, use-a
            const anyWin = window as any
            if (typeof anyWin.requestNotificationPermission === "function") {
              const p = await anyWin.requestNotificationPermission()
              if (typeof p === "string") {
                permission = p as NotificationPermission
              } else if (typeof Notification !== "undefined") {
                permission = Notification.permission
              }
            } else if ("Notification" in window && typeof Notification.requestPermission === "function") {
              permission = await Notification.requestPermission()
            } else {
              permission = "default"
            }
          } catch {
            permission = typeof Notification !== "undefined" ? Notification.permission : "default"
          } finally {
            // responde ao iframe com o resultado; o quiz só avança depois disso
            iframe?.contentWindow?.postMessage(
              { source: "quizAI", type: "notification-result", permission },
              "*"
            )
          }
        }

        ask()
        return
      }
    }
    window.addEventListener("message", msgHandler)
  }

  function open(): void {
    if (opened) return
    buildUI()
    if (!container) return
    opened = true
    container.style.display = "block"
    document.body.style.overflow = "hidden" // trava scroll do fundo
  }

  function hardClose(): void {
    if (!opened) return
    opened = false

    try {
      if (container) container.remove()
      document.body.style.overflow = ""
    } catch {}

    if (escHandler) document.removeEventListener("keydown", escHandler)
    if (msgHandler) window.removeEventListener("message", msgHandler)
    escHandler = msgHandler = null
  }

  function close(): void {
    if (!opened) return
    try {
      const msg: QuizAIMessage = { source: "quizAI", type: "request-close" }
      iframe?.contentWindow?.postMessage(msg, "*")
      setTimeout(hardClose, 250)
    } catch {
      hardClose()
    }
  }

  // auto-open com delay
  if (delay > 0) setTimeout(open, delay)

  // API global
  const w = window as Window & { quizAI?: QuizAIAPI }
  w.quizAI = w.quizAI || { open, close }
  w.quizAI.open = open
  w.quizAI.close = close
})()
